import { useGLTF, useTexture } from '@react-three/drei';
import { DecalGeometry } from 'three/examples/jsm/geometries/DecalGeometry';
import { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';

import { useRecoilValue } from 'recoil';
import { logoTransformState } from 'state/langState';
import { getSafeTransform } from 'utils/getText';

export default function TshirtDecalLogo({ logo, product }) {
  const { scene, nodes } = useGLTF('/obj/t-shirt.glb');

  const texture = useTexture(logo);

  const transforms = useRecoilValue(logoTransformState);
  const transform = getSafeTransform(transforms, product);

  /*
    geometry refs
  */

  const geometryRefs = useRef([]);
  const [geometries, setGeometries] = useState([]);

  /*
    target meshes — 1회만
  */

  const targetMeshes = useMemo(
    () => [nodes.Object_4, nodes.Object_5].filter(Boolean),
    [nodes]
  );

  /*
    reusable objects (GC 방지)
  */

  const raycasterRef = useRef(new THREE.Raycaster());
  const originRef = useRef(new THREE.Vector3());
  const directionRef = useRef(new THREE.Vector3(0, 0, 1));
  const quaternionRef = useRef(new THREE.Quaternion());
  const sizeRef = useRef(new THREE.Vector3());
  const baseVectorRef = useRef(new THREE.Vector3(0, 0, 1));

  /*
    texture base 설정 — 1회
  */

  useEffect(() => {
    if (!texture) return;

    texture.flipY = false;

    texture.wrapS = THREE.ClampToEdgeWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;

    texture.anisotropy = 8;

    texture.needsUpdate = true;
  }, [texture]);

  /*
    front texture clone — 1회
  */

  const frontTexture = useMemo(() => {
    if (!texture) return null;

    const t = texture.clone();

    t.flipY = false;

    t.wrapS = THREE.ClampToEdgeWrapping;
    t.wrapT = THREE.ClampToEdgeWrapping;

    t.center.set(0.5, 0.5);

    /*
      핵심: 좌우 반전
    */

    t.repeat.set(-1, 1);

    t.anisotropy = 8;

    t.needsUpdate = true;

    return t;
  }, [texture]);

  /*
    decal 생성
  */

  useEffect(() => {
    if (!targetMeshes.length) return;

    /*
      이전 geometry dispose
    */

    geometryRefs.current.forEach((g) => g.dispose());
    geometryRefs.current = [];

    const newGeometries = [];

    const { x, y } = transform.position;

    const depth = transform.depth ?? 0.02;
    const scale = transform.scale ?? 1;

    const raycaster = raycasterRef.current;
    const origin = originRef.current;
    const direction = directionRef.current;
    const quaternion = quaternionRef.current;
    const size = sizeRef.current;
    const baseVector = baseVectorRef.current;

    origin.set(-x, y, -5);

    raycaster.set(origin, direction);

    size.set(scale, scale, scale);

    targetMeshes.forEach((mesh) => {
      mesh.updateWorldMatrix(true, true);

      const intersects = raycaster.intersectObject(mesh, true);

      if (!intersects.length) return;

      const hit = intersects[0];

      /*
        normal
      */

      const normal = hit.face.normal.clone();

      normal.transformDirection(hit.object.matrixWorld);

      /*
        position
      */

      const finalPosition = hit.point.clone().add(normal.multiplyScalar(depth));

      /*
        rotation
      */

      quaternion.setFromUnitVectors(baseVector, normal);

      const rotation = new THREE.Euler().setFromQuaternion(quaternion);

      rotation.z = Math.PI;

      /*
        geometry 생성
      */

      const geometry = new DecalGeometry(mesh, finalPosition, rotation, size);

      geometryRefs.current.push(geometry);

      newGeometries.push({
        geometry,
        meshName: mesh.name,
      });
    });

    setGeometries(newGeometries);

    /*
      cleanup
    */

    return () => {
      geometryRefs.current.forEach((g) => g.dispose());
      geometryRefs.current = [];
    };
  }, [
    targetMeshes,
    transform.position.x,
    transform.position.y,
    transform.depth,
    transform.scale,
  ]);

  /*
    render
  */

  return (
    <>
      <primitive object={scene} scale={4} position={[0, -5, 0]} />

      {geometries.map(({ geometry, meshName }, i) => (
        <mesh key={i} geometry={geometry}>
          <meshStandardMaterial
            map={meshName === 'Object_4' ? frontTexture : texture}
            transparent
            depthWrite={false}
            polygonOffset
            polygonOffsetFactor={-4}
          />
        </mesh>
      ))}
    </>
  );
}

useGLTF.preload('/obj/t-shirt.glb');
